import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { trackMetaCustomEvent, trackMetaEvent } from '@/lib/metaPixel';
import { ContactRequestDialog } from '@/components/vsl/ContactRequestDialog';
import { captureTrackingParams } from '@/lib/trackingParams';
import {
  ArrowRight, Calendar, CheckCircle, Magnet, MessageSquare, Workflow,
  Settings2, Bot, Quote, Calculator, FileText, Receipt, PenLine, ShieldCheck,
  Users, Clock, FileQuestion, PhoneCall, LineChart, BadgeCheck,
} from 'lucide-react';

const WISTIA_PLAYER_SCRIPT_ID = 'wistia-player-script';
const BOOKING_URL = 'https://calendar.notion.so/meet/agencepulsia/dl44u4pda';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const functions = [
  {
    icon: Magnet,
    title: 'Acquisition',
    description: 'Attirer vos futurs clients avec des contenus et des campagnes pensés pour votre marché local.',
  },
  {
    icon: MessageSquare,
    title: 'Conversion',
    description: 'Engager la conversation et qualifier chaque demande, jour et nuit.',
  },
  {
    icon: Workflow,
    title: 'Suivi commercial',
    description: 'Accompagner chaque demande du premier message jusqu\'au rendez-vous, sans rien oublier.',
  },
  {
    icon: Settings2,
    title: 'Maintenance',
    description: 'Optimiser le système mois après mois à partir de vos résultats réels.',
  },
];

const cases = [
  {
    sector: '2D Traiteur · Traiteur événementiel à La Réunion',
    context: 'Avant : des contrats au rythme des recommandations.',
    stats: [
      { value: '+1M', label: 'de vues en quelques mois' },
      { value: '192', label: 'demandes de devis / mois' },
      { value: '5 à 8', label: 'contrats signés en plus / mois' },
    ],
    detail: 'Des contrats de 3 000 à 18 000 €. Aujourd\'hui, il y a plus de demandes que de place.',
    quote: 'C\'est incroyable.',
    author: 'Cédric, co-gérant',
  },
  {
    sector: 'Gonflable 974 · Location de structures gonflables',
    context: 'Avant : un carnet papier, des messages sans réponse, des réservations perdues.',
    stats: [
      { value: 'x2+', label: 'réservations' },
      { value: '3-4 mois', label: 'd\'agenda rempli à l\'avance' },
      { value: '24/7', label: 'agent IA connecté aux disponibilités' },
    ],
    detail: 'Le temps gagné leur permet d\'ajouter de nouvelles prestations.',
    quote: 'La meilleure acquisition que j\'ai pu faire.',
    author: 'Kevin, gérant',
  },
];

const criteria = [
  'Un ticket moyen d\'au moins 500 € par prestation',
  'De l\'expérience et une offre qui trouve déjà ses clients',
  'La capacité de servir les clients que nous vous apportons',
  'Une vraie envie de croissance',
];

const pains = [
  { icon: Users, label: 'Vos clients viennent surtout du bouche-à-oreille : ça marche, mais ce n\'est ni fiable ni suffisant.' },
  { icon: Clock, label: 'Vous passez des heures à répondre aux mêmes questions.' },
  { icon: FileQuestion, label: 'Vos devis partent, les réponses tardent à revenir.' },
];

const preparation = [
  { icon: FileText, label: 'Vos prestations et leurs prix' },
  { icon: Receipt, label: 'La façon dont vous facturez' },
  { icon: PenLine, label: 'Le nombre de devis que vous signez sur 10' },
];

const nextSteps = [
  { icon: PhoneCall, label: 'Vous faites votre demande, nous vous rappelons au créneau de votre choix' },
  { icon: LineChart, label: 'Nous projetons 3 scénarios sur 12 mois à partir de vos chiffres' },
  { icon: BadgeCheck, label: 'Si ce n\'est pas rentable pour vous, je vous le dis' },
];

const VARIANTS = {
  // Envoyée après la prise de rendez-vous
  booked: {
    mediaId: 'k3dcjyag6j',
    documentTitle: 'Pulsia Autopilote | Présentation avant rendez-vous',
    heroLabel: 'À regarder avant notre rendez-vous',
    heroIntro: 'D\'où vient la méthode, ce qu\'elle produit chez nos clients, et comment nous vérifierons ensemble si elle est rentable pour votre activité.',
    ctaLabel: 'Réserver mon rendez-vous',
    ctaAction: 'booking',
    heroNote: 'Vous avez déjà réservé ? Parfait, préparez simplement les 3 éléments plus bas.',
    showPains: false,
    finalTitle: (
      <>Pendant le rendez-vous : <span className="gradient-text">une simulation de rentabilité</span></>
    ),
    finalIntro: 'À partir de vos prestations, de vos prix et de votre taux de signature, nous projetons 3 scénarios sur 12 mois. Si l\'accompagnement n\'est pas rentable pour vous, je vous le dirai.',
    finalListTitle: 'Pour gagner du temps, préparez :',
    finalList: preparation,
  },
  // Envoyée avant la prise de rendez-vous
  invitation: {
    mediaId: '3uk8ehid3m',
    documentTitle: 'Pulsia Autopilote | Présentation',
    heroLabel: 'Présentation vidéo',
    heroIntro: 'D\'où vient la méthode, ce qu\'elle produit chez nos clients, et comment nous pouvons vérifier ensemble si elle est rentable pour votre activité.',
    ctaLabel: 'Je demande mon rendez-vous',
    ctaAction: 'contactForm',
    heroNote: '30 secondes pour faire votre demande. Nous vous rappelons pour fixer le rendez-vous.',
    showPains: true,
    finalTitle: (
      <>Vous vous reconnaissez dans ce portrait ? <span className="gradient-text">Prenons rendez-vous.</span></>
    ),
    finalIntro: 'Ensemble, nous ferons une simulation de rentabilité à partir de vos prestations, de vos prix et de votre taux de signature.',
    finalListTitle: 'Comment ça se passe :',
    finalList: nextSteps,
  },
};

function CtaButton({ label, location, pagePath, action, onOpenForm }) {
  if (action === 'contactForm') {
    const handleOpen = () => {
      trackMetaCustomEvent('ContactFormOpen', {
        page_path: pagePath,
        cta_label: label,
        cta_location: location,
      });
      onOpenForm();
    };

    return (
      <Button variant="hero" size="xl" className="btn-glow group" onClick={handleOpen}>
        <PhoneCall className="w-5 h-5" />
        {label}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </Button>
    );
  }

  const handleClick = () => {
    trackMetaEvent('Schedule', {
      destination: BOOKING_URL,
      page_path: pagePath,
      cta_label: label,
      cta_location: location,
    });
  };

  return (
    <a
      href={BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      data-local-schedule-tracked="true"
    >
      <Button variant="hero" size="xl" className="btn-glow group">
        <Calendar className="w-5 h-5" />
        {label}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </Button>
    </a>
  );
}

export function AutopiloteVsl({ variant }) {
  const config = VARIANTS[variant];
  const { mediaId } = config;
  const { pathname } = useLocation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const openForm = () => setIsFormOpen(true);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = config.documentTitle;

    let robotsMeta = document.querySelector('meta[name="robots"]');
    const previousRobotsContent = robotsMeta?.getAttribute('content') ?? null;

    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex,nofollow,noarchive');

    captureTrackingParams();

    if (!document.getElementById(WISTIA_PLAYER_SCRIPT_ID)) {
      const playerScript = document.createElement('script');
      playerScript.id = WISTIA_PLAYER_SCRIPT_ID;
      playerScript.src = 'https://fast.wistia.com/player.js';
      playerScript.async = true;
      document.body.appendChild(playerScript);
    }

    const mediaScriptId = `wistia-media-${mediaId}`;
    if (!document.getElementById(mediaScriptId)) {
      const mediaScript = document.createElement('script');
      mediaScript.id = mediaScriptId;
      mediaScript.src = `https://fast.wistia.com/embed/${mediaId}.js`;
      mediaScript.type = 'module';
      mediaScript.async = true;
      document.body.appendChild(mediaScript);
    }

    return () => {
      document.title = previousTitle;

      if (previousRobotsContent === null) {
        robotsMeta.remove();
      } else {
        robotsMeta.setAttribute('content', previousRobotsContent);
      }
    };
  }, [config.documentTitle, mediaId]);

  return (
    <div className="min-h-screen flex flex-col">
      <style>{`
        wistia-player[media-id='${mediaId}']:not(:defined) {
          background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/${mediaId}/swatch');
          display: block;
          filter: blur(5px);
          padding-top: 56.25%;
        }
      `}</style>

      {/* Header minimal : pas de navigation pour garder l'attention sur la vidéo */}
      <header className="absolute top-0 left-0 right-0 z-50 py-5">
        <div className="section-container flex justify-center">
          <img
            src="\wp-content\uploads\2024\05\LOGO-PULSIA-1d.png"
            alt="Pulsia.ai"
            className="h-10 w-auto"
          />
        </div>
      </header>

      <main className="flex-1">
        {/* Hero + Vidéo */}
        <section className="relative pt-28 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-hero" />
          <div className="hero-glow" />
          <div className="absolute inset-0 bg-grid-pattern opacity-30" />

          <div className="section-container relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <span className="section-label mb-4 inline-block">
                {config.heroLabel}
              </span>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                <span className="gradient-text">Pulsia Autopilote</span> : le système qui attire, qualifie et
                convertit vos clients pendant que vous travaillez
              </h1>
              <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
                {config.heroIntro}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="max-w-5xl mx-auto"
            >
              <div className="rounded-2xl overflow-hidden border border-primary/20 shadow-lg glow-primary bg-card">
                <wistia-player media-id={mediaId} seo="false" aspect="1.7777777777777777" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 flex flex-col items-center gap-3"
            >
              <CtaButton label={config.ctaLabel} location="hero" pagePath={pathname} action={config.ctaAction} onOpenForm={openForm} />
              <p className="text-sm text-muted-foreground text-center">
                {config.heroNote}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Vous vous reconnaissez ? */}
        {config.showPains && (
          <section className="py-20">
            <div className="section-container">
              <motion.div {...fadeUp} className="text-center mb-12">
                <span className="section-label mb-4 inline-block">Parlons de vous</span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
                  Ce qui manque, <span className="gradient-text">c'est un système</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Agence de communication, publications boostées, prospection à froid… Ce n'est pas un manque
                  d'effort de votre part : aucune de ces méthodes ne suffit seule.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {pains.map((item, index) => (
                  <motion.div
                    key={item.label}
                    {...fadeUp}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="p-6 rounded-xl card-gradient"
                  >
                    <div className="feature-icon mb-5">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-foreground">{item.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Les 4 fonctions */}
        <section className="py-20">
          <div className="section-container">
            <motion.div {...fadeUp} className="text-center mb-14">
              <span className="section-label mb-4 inline-block">La méthode</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Un système en <span className="gradient-text">4 fonctions</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Les méthodes des leaders du marketing digital, transposées à la réalité d'une entreprise
                de service locale. Avec Alxio, notre agent d'intelligence artificielle, au centre.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {functions.map((item, index) => (
                <motion.div
                  key={item.title}
                  {...fadeUp}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 rounded-xl card-gradient card-hover"
                >
                  <div className="feature-icon mb-5">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              {...fadeUp}
              className="max-w-3xl mx-auto flex items-center gap-4 p-5 rounded-xl bg-primary/5 border border-primary/20"
            >
              <Bot className="w-8 h-8 text-primary shrink-0" />
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Alxio relie les 4 fonctions.</span>{' '}
                Et si votre activité le permet, le système vous ouvre les portes pour élargir votre zone de chalandise.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Résultats */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-subtle" />
          <div className="absolute inset-0 bg-dots-pattern opacity-20" />

          <div className="section-container relative z-10">
            <motion.div {...fadeUp} className="text-center mb-14">
              <span className="section-label mb-4 inline-block">Résultats clients</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
                Ce que le système <span className="gradient-text">produit</span>
              </h2>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {cases.map((item, index) => (
                <motion.div
                  key={item.sector}
                  {...fadeUp}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="p-8 rounded-2xl card-gradient flex flex-col"
                >
                  <p className="text-xs font-medium uppercase tracking-wider text-primary mb-2">
                    {item.sector}
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">{item.context}</p>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {item.stats.map((stat) => (
                      <div key={stat.label}>
                        <div className="font-display text-2xl sm:text-3xl font-bold gradient-text">
                          {stat.value}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  <p className="text-foreground mb-6">{item.detail}</p>

                  <div className="mt-auto pt-6 border-t border-border/50 flex gap-3">
                    <Quote className="w-6 h-6 text-primary/40 shrink-0" />
                    <div>
                      <p className="font-display text-lg text-foreground">« {item.quote} »</p>
                      <p className="text-sm text-muted-foreground">{item.author}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pour qui */}
        <section className="py-20">
          <div className="section-container">
            <motion.div
              {...fadeUp}
              className="max-w-4xl mx-auto p-8 lg:p-12 rounded-2xl card-gradient"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="feature-icon">
                  <ShieldCheck className="w-6 h-6 text-primary" />
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
                  Nous ne prenons pas tous les dossiers
                </h2>
              </div>
              <p className="text-muted-foreground mb-8">
                Nous n'activons pas l'acquisition sur une offre qui n'a pas fait ses preuves. Pour avancer ensemble, il faut :
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {criteria.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Le rendez-vous */}
        <section className="py-24">
          <div className="section-container">
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-card via-card to-secondary/20 border border-border/50 p-8 lg:p-16"
            >
              <div className="hero-glow" />
              <div className="relative z-10 max-w-3xl mx-auto text-center">
                <div className="feature-icon mx-auto mb-6">
                  <Calculator className="w-6 h-6 text-primary" />
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
                  {config.finalTitle}
                </h2>
                <p className="text-lg text-muted-foreground mb-10">
                  {config.finalIntro}
                </p>

                <p className="text-sm font-medium uppercase tracking-wider text-primary mb-4">
                  {config.finalListTitle}
                </p>
                <div className="grid sm:grid-cols-3 gap-4 mb-12 text-left">
                  {config.finalList.map((item) => (
                    <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10">
                      <item.icon className="w-5 h-5 text-primary shrink-0" />
                      <span className="text-sm text-foreground">{item.label}</span>
                    </div>
                  ))}
                </div>

                <p className="font-display text-xl text-foreground mb-8">
                  Pas de magie. Des chiffres, <span className="gradient-text">les vôtres.</span>
                </p>
                <CtaButton label={config.ctaLabel} location="footer_cta" pagePath={pathname} action={config.ctaAction} onOpenForm={openForm} />
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />

      {config.ctaAction === 'contactForm' && (
        <ContactRequestDialog open={isFormOpen} onOpenChange={setIsFormOpen} />
      )}
    </div>
  );
}
